import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Helmet } from "react-helmet";
import { Calendar, User, Share2, ArrowRight, CheckCircle, ExternalLink } from 'lucide-react';
import RenderGutenberg from './../components/RenderGutenberg';
import Loader from '../components/Loader';
import SnakeFlowGallery from '../components/SnakeFlowGallery';

const SingleProduct = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true)
    const [details, setDetails] = useState(null)
    const [error, setError] = useState(null)
    const [showShare, setShowShare] = useState(false)

    useEffect(() => {
        const fetchData = () => {
            setLoading(true)
            axios.get(`posts?slug=${slug}`)
                .then((response) => {
                    console.log('Product Data:', response.data[0]);
                    setDetails(response.data[0])
                    setLoading(false)
                })
                .catch((err) => {
                    console.error(err)
                    setError(err)
                    setLoading(false)
                });
        }

        fetchData()
        window.scrollTo(0, 0);
    }, [slug])

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    }

    const shareLinks = {
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(details?.title?.rendered || '')}&url=${encodeURIComponent(window.location.href)}`,
        linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(details?.title?.rendered || '')}`,
    }

    // Helper function to check if SCF data exists
    const hasSCFData = () => {
        return details?.acf && Object.keys(details.acf).length > 0 &&
            !(details.acf.length === 0 || Object.keys(details.acf).length === 0);
    }

    // Helper function to extract image URL from object or string
    const getImageUrl = (imageField) => {
        if (!imageField) return null;
        if (typeof imageField === 'string') return imageField;
        if (typeof imageField === 'object' && imageField.url) return imageField.url;
        return null;
    }

    // Get product subtitle
    const getSubtitle = () => {
        if (hasSCFData() && details.acf.product_subtitle) {
            return details.acf.product_subtitle;
        }
        return '';
    }

    // Get product icon/image for hero section
    const getHeroImage = () => {
        if (hasSCFData() && details.acf.product_icon) {
            const iconUrl = getImageUrl(details.acf.product_icon);
            if (iconUrl) return iconUrl;
        }
        const seoImage = getImageUrl(details.acf?.seo_image_posts);
        if (seoImage) return seoImage;
        return 'https://via.placeholder.com/600x400?text=Product+Image';
    }

    // Get key features from SCF with proper image URLs
    const getKeyFeatures = () => {
        if (hasSCFData() && details.acf.key_features && details.acf.key_features.length > 0) {
            return details.acf.key_features.map(feature => ({
                ...feature,
                feature_icon_url: getImageUrl(feature.feature_icon)
            }));
        }
        return [];
    }

    // Get gallery items for Snake Flow Gallery
    const getGalleryItems = () => {
        if (hasSCFData() && details.acf.feature_gallery && details.acf.feature_gallery.length > 0) {
            return details.acf.feature_gallery.map(item => ({
                image: getImageUrl(item.image),
                description: item.description || ''
            })).filter(item => item.image);
        }
        return [];
    }

    // Get description/excerpt
    const getDescription = () => {
        if (hasSCFData() && details.acf.product_short_desc) {
            return details.acf.product_short_desc;
        }
        return details.acf?.SEO_excerpt_posts || details.excerpt?.rendered?.replace(/<[^>]*>/g, '').substring(0, 200);
    }

    if (loading) {
        return <Loader />;
    }

    if (error || !details) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background text-foreground transition-colors duration-300">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="text-6xl mb-6">😕</div>
                    <h2 className="text-2xl font-bold font-heading mb-2">Product Target Not Found</h2>
                    <p className="text-muted-foreground mb-6 font-body">The architectural blueprint you're looking for could not be located in our database.</p>
                    <button
                        onClick={() => navigate('/products')}
                        className="px-8 py-3 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-full font-bold shadow-lg hover:shadow-[0_0_20px_rgba(var(--color-primary),0.5)] transition-all font-secondary"
                    >
                        Return to Products
                    </button>
                </div>
            </div>
        );
    }

    const keyFeatures = getKeyFeatures();
    const galleryItems = getGalleryItems();

    return (
        <>
            <Helmet>
                <title>{`${details.title.rendered} - Connected Bytes Software Service`}</title>
                <meta name="description" content={details.acf?.SEO_excerpt_posts || details.excerpt?.rendered?.replace(/<[^>]*>/g, '').substring(0, 160)} />
                <meta property="og:title" content={`${details.title.rendered} - Connected Bytes Software Service`} />
                <meta property="og:description" content={details.acf?.SEO_excerpt_posts || ''} />
                <meta property="og:image" content={getHeroImage()} />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:type" content="article" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${details.title.rendered} - Connected Bytes Software Service`} />
                <meta name="twitter:description" content={details.acf?.SEO_excerpt_posts || ''} />
                <meta name="twitter:image" content={getHeroImage()} />
            </Helmet>

            <div className="bg-background text-foreground min-h-screen transition-colors duration-300">
                {/* Hero Section - Reduced padding */}
                <div className="relative pt-24 pb-12 overflow-hidden border-b border-border">
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 blur-[150px] rounded-full pointer-events-none translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/5 blur-[120px] rounded-full pointer-events-none -translate-x-1/2 translate-y-1/2" />

                    <div className="max-w-7xl mx-auto px-4 relative z-10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            {/* Left Content */}
                            <motion.div
                                initial={{ opacity: 0, x: -50, filter: 'blur(10px)' }}
                                animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                                transition={{ duration: 0.8, type: 'spring' }}
                            >
                                {/* {getSubtitle() && (
                                    <span className="inline-block text-primary font-bold text-xs uppercase tracking-widest font-secondary mb-3 px-3 py-1 bg-primary/10 border border-primary/20 rounded-full">
                                        {getSubtitle()}
                                    </span>
                                )} */}

                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 leading-tight font-heading">
                                    {details.title.rendered}
                                </h1>

                                <p className="text-lg md:text-xl text-muted-foreground mb-6 leading-relaxed font-body border-l-2 border-primary pl-4">
                                    {getDescription()}
                                </p>



                                {showShare && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mt-4 flex flex-wrap gap-3"
                                    >
                                        <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 bg-[#1DA1F2]/10 text-[#1DA1F2] border border-[#1DA1F2]/30 rounded-full text-sm font-bold hover:bg-[#1DA1F2] hover:text-white transition-colors duration-300">Twitter X</a>
                                        <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 bg-[#0077B5]/10 text-[#0077B5] border border-[#0077B5]/30 rounded-full text-sm font-bold hover:bg-[#0077B5] hover:text-white transition-colors duration-300">LinkedIn</a>
                                        <button onClick={() => { navigator.clipboard.writeText(window.location.href); alert('Link copied!'); }} className="px-5 py-2.5 bg-muted text-foreground border border-border rounded-full text-sm font-bold hover:bg-foreground hover:text-background transition-colors duration-300">Copy Link</button>
                                    </motion.div>
                                )}
                            </motion.div>

                            {/* Right Image */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95, filter: 'blur(20px)' }}
                                animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                                transition={{ duration: 0.8, delay: 0.2, type: 'spring' }}
                                className="relative lg:h-[450px]"
                            >
                                <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-accent/20 rounded-3xl blur-2xl transform scale-105 pointer-events-none -z-10" />
                                <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-card border border-border h-full flex items-center justify-center p-6 group">
                                    <img
                                        src={getHeroImage()}
                                        alt={details.title.rendered}
                                        className="w-full h-full object-contain drop-shadow-2xl group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent pointer-events-none mix-blend-overlay" />
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>

                {/* Key Features Section - Reduced padding, white cards */}
                {keyFeatures.length > 0 && (
                    <div className="py-12 relative border-b border-border">
                        <div className="max-w-7xl mx-auto px-4 relative z-10">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                viewport={{ once: true }}
                                className="text-center mb-10"
                            >
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-3 font-heading">Key Feature</h2>
                                <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full shadow-[0_0_10px_rgba(var(--color-primary),0.5)]" />
                                <p className="text-muted-foreground mt-4 max-w-2xl mx-auto font-body">
                                    The fundamental components making this an elite solution.
                                </p>
                            </motion.div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {keyFeatures.map((feature, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: idx * 0.1 }}
                                        viewport={{ once: true }}
                                        className="bg-white dark:bg-card rounded-2xl p-6 border border-border shadow-sm hover:shadow-[0_0_30px_rgba(var(--color-primary),0.1)] hover:border-primary/50 transition-all duration-300 group h-full relative overflow-hidden"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                                        <div className="flex flex-col gap-4 relative z-10">
                                            <div className="flex items-start gap-4">
                                                {feature.feature_icon_url ? (
                                                    <div className="w-12 h-12 bg-gray-50 dark:bg-background rounded-xl p-2 shadow-inner border border-border/50 flex-shrink-0 group-hover:scale-110 transition-transform duration-300 flex items-center justify-center">
                                                        <img
                                                            src={feature.feature_icon_url}
                                                            alt={feature.feature_title}
                                                            className="w-full h-full object-contain"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-inner">
                                                        <CheckCircle className="w-5 h-5 text-primary" />
                                                    </div>
                                                )}
                                                <h3 className="text-lg font-bold font-heading line-clamp-2 mt-1 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-accent transition-colors duration-300">
                                                    {feature.feature_title}
                                                </h3>
                                            </div>
                                            {feature.feature_description && (
                                                <p className="text-muted-foreground text-sm font-body leading-relaxed group-hover:text-foreground/80 transition-colors duration-300">
                                                    {feature.feature_description}
                                                </p>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* How It Works - Snake Flow Gallery Section - Fixed flickering */}
                {galleryItems.length >= 6 && (
                    <div className="py-12 relative overflow-hidden">
                        <div className="max-w-7xl mx-auto px-4 relative z-10">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                viewport={{ once: true }}
                                className="text-center mb-10"
                            >
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-3 font-heading">Execution Flow</h2>
                                <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full shadow-[0_0_10px_rgba(var(--color-primary),0.5)]" />
                                <p className="text-muted-foreground mt-4 max-w-2xl mx-auto font-body">
                                    Analyze the step-by-step modular progression sequence.
                                </p>
                            </motion.div>

                            <SnakeFlowGallery items={galleryItems} />
                        </div>
                    </div>
                )}

                {/* Fallback Gallery (if less than 6 images) - White cards */}
                {galleryItems.length > 0 && galleryItems.length < 6 && (
                    <div className="py-12 border-b border-border relative overflow-hidden">
                        <div className="max-w-7xl mx-auto px-4 relative z-10">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                viewport={{ once: true }}
                                className="text-center mb-10"
                            >
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-3 font-heading">Visual Schema</h2>
                                <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full shadow-[0_0_10px_rgba(var(--color-primary),0.5)]" />
                                <p className="text-muted-foreground mt-4 max-w-2xl mx-auto font-body">
                                    UI/UX architecture captures in operation.
                                </p>
                            </motion.div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {galleryItems.map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: idx * 0.1 }}
                                        viewport={{ once: true }}
                                        className="group cursor-pointer block bg-white dark:bg-card rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                                        onClick={() => window.open(item.image, '_blank')}
                                    >
                                        <div className="relative overflow-hidden">
                                            <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 mix-blend-overlay z-10 pointer-events-none" />
                                            <img
                                                src={item.image}
                                                alt={`Interface preview ${idx + 1}`}
                                                className="w-full h-56 object-cover transform group-hover:scale-110 transition-transform duration-700"
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                                                <div className="bg-background/80 backdrop-blur-md p-2.5 rounded-full text-foreground shadow-lg border border-border/50">
                                                    <ExternalLink className="w-4 h-4" />
                                                </div>
                                            </div>
                                        </div>
                                        {item.description && (
                                            <p className="text-muted-foreground font-body text-sm text-center p-4 pt-3 border-t border-border/50">
                                                {item.description}
                                            </p>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content Section - Reduced padding */}
                <div className="max-w-7xl mx-auto px-4 py-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-heading prose-headings:font-black prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl prose-p:font-body prose-p:leading-relaxed prose-a:text-primary prose-a:font-bold prose-img:rounded-3xl prose-img:shadow-2xl prose-img:border prose-img:border-border"
                    >
                        <div className=" dark:bg-card/30 backdrop-blur-sm p-8 md:p-10 border border-slate-300 rounded-3xl shadow-sm">
                            {hasSCFData() && details.acf.detailed_content ? (
                                <RenderGutenberg content={details.acf.detailed_content} />
                            ) : (
                                <RenderGutenberg content={details.content.rendered} />
                            )}
                        </div>
                    </motion.div>
                </div>

                {/* CTA Section - Reduced padding */}
                <div className="py-16 relative overflow-hidden border-t border-border">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary/5 pointer-events-none" />

                    <div className="max-w-4xl mx-auto text-center px-4 relative z-10">
                        <h2 className="text-3xl md:text-4xl font-black font-heading mb-4">Implement This Architecture</h2>
                        <p className="text-lg text-muted-foreground mb-8 font-body max-w-2xl mx-auto">
                            Transform your infrastructure rapidly. Our engineers are ready to commence deployment.
                        </p>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/contact')}
                            className="px-8 py-4 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-full font-bold shadow-[0_0_20px_rgba(var(--color-primary),0.3)] hover:shadow-[0_0_30px_rgba(var(--color-primary),0.5)] transition-all duration-300 inline-flex items-center gap-2 font-secondary"
                        >
                            Initiate Contact <ArrowRight className="w-5 h-5" />
                        </motion.button>
                    </div>
                </div>

                {/* Related Products - Reduced padding */}
                <div className="bg-muted/5 py-12 border-t border-border">
                    <div className="max-w-7xl mx-auto px-4 text-center">
                        <h2 className="text-2xl md:text-3xl font-black font-heading mb-3">Explore More Modules</h2>
                        <div className="w-20 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full mb-8 shadow-[0_0_10px_rgba(var(--color-primary),0.5)]" />
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate('/products')}
                            className="px-6 py-3 bg-background text-foreground border-2 border-border hover:border-primary hover:text-primary rounded-full font-bold shadow-sm transition-all duration-300 inline-flex items-center gap-2 font-secondary"
                        >
                            View Architecture Catalog <ArrowRight className="w-4 h-4" />
                        </motion.button>
                    </div>
                </div>
            </div>
        </>
    )
}

export default SingleProduct